import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import HubPage from "./pages/HubPage";
import PrescriptionsPage from "./pages/PrescriptionsPage";
import NewPrescriptionPage from "./pages/NewPrescriptionPage";
import PrescriptionDetailPage from "./pages/PrescriptionDetailPage";
import TodoPage from "./pages/TodoPage";
import ShoppingPage from "./pages/ShoppingPage";
import SettingsPage from "./pages/SettingsPage";
import NotesPage from "./pages/NotesPage";
import UnitConverterPage from "./pages/UnitConverterPage";
import ImageEditorPage from "./pages/ImageEditorPage";
import QRGeneratorPage from "./pages/QRGeneratorPage";
import CalendarGeneratorPage from "./pages/CalendarGeneratorPage";
import SharePage from "./pages/SharePage";
import ThankYouPage from "./pages/ThankYouPage";
import PrivacyPage from "./pages/PrivacyPage";
import TokensPage from "./pages/TokensPage";
import InboxPage from "./pages/InboxPage";
import RoutinesPage from "./pages/RoutinesPage";
import LifeAdminPage from "./pages/LifeAdminPage";
import TextToChecklistPage from "./pages/TextToChecklistPage";
import UrlSummaryPage from "./pages/UrlSummaryPage";
import OcrPage from "./pages/OcrPage";
import MemoryPage from "./pages/MemoryPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AccountPage from "./pages/AccountPage";
import FamilyPage from "./pages/FamilyPage";
import JoinFamilyPage from "./pages/JoinFamilyPage";
import DinnerPlatePage from "./pages/DinnerPlatePage";
import DinnerPlateNewPage from "./pages/DinnerPlateNewPage";
import DinnerPlateEventPage from "./pages/DinnerPlateEventPage";
import DinnerPlateGuestPage from "./pages/DinnerPlateGuestPage";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ReminderPopup from "./components/ReminderPopup";
import PreviewBanner from "./components/PreviewBanner";
import { startReminderPolling, setPopupHandler } from "./lib/reminders";
import { registerServiceWorker } from "./lib/pwa";
import { applyTheme, getTheme } from "./lib/theme";
import { loadAnalytics } from "./lib/analytics";
import { loadEthicalAds } from "./lib/ethicalads";
import { loadAdSense } from "./lib/adsense";
import { loadRuntimeConfig } from "./lib/runtimeConfig";
import { loadFxRates } from "./lib/currency";

function App() {
  const [popupData, setPopupData] = useState(null);

  useEffect(() => {
    applyTheme(getTheme());
    setPopupHandler((d) => setPopupData(d));
    startReminderPolling();
    registerServiceWorker();
    // Load runtime config first so GA/AdSense see admin overrides before they try to init
    loadRuntimeConfig().then(() => {
      loadAnalytics();
      loadEthicalAds();
      loadAdSense();
    });
    loadFxRates();
    // Fade out the splash once React has mounted. Keep a minimum display time
    // (600 ms) so the heartbeat animation doesn't get cut off on fast connections.
    const splash = document.getElementById("verihandi-splash");
    if (splash) {
      const hideAt = performance.now() + 600;
      const hide = () => {
        splash.classList.add("vh-fade");
        setTimeout(() => splash.remove(), 450);
      };
      const delay = Math.max(0, hideAt - performance.now());
      setTimeout(hide, delay);
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PreviewBanner />
          <Routes>
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/dinner-plate/e/:slug" element={<DinnerPlateGuestPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/_vh-admin" element={<AdminLoginPage />} />
            <Route path="/_vh-admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="*" element={
              <Layout>
                <Routes>
                  <Route path="/" element={<HubPage />} />
                  <Route path="/prescriptions" element={<PrescriptionsPage />} />
                  <Route path="/prescriptions/new" element={<NewPrescriptionPage />} />
                  <Route path="/prescriptions/:id" element={<PrescriptionDetailPage />} />
                  <Route path="/todo" element={<TodoPage />} />
                  <Route path="/shopping" element={<ShoppingPage />} />
                  <Route path="/notes" element={<NotesPage />} />
                  <Route path="/tools/unit-converter" element={<UnitConverterPage />} />
                  <Route path="/tools/image-editor" element={<ImageEditorPage />} />
                  <Route path="/tools/qr-generator" element={<QRGeneratorPage />} />
                  <Route path="/tools/calendar-generator" element={<CalendarGeneratorPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/tokens" element={<TokensPage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/routines" element={<RoutinesPage />} />
                  <Route path="/life-admin" element={<LifeAdminPage />} />
                  <Route path="/tools/text-to-checklist" element={<TextToChecklistPage />} />
                  <Route path="/tools/url-summary" element={<UrlSummaryPage />} />
                  <Route path="/tools/ocr" element={<OcrPage />} />
                  <Route path="/memory" element={<MemoryPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/family" element={<FamilyPage />} />
                  <Route path="/family/join" element={<JoinFamilyPage />} />
                  <Route path="/tools/dinner-plate" element={<DinnerPlatePage />} />
                  <Route path="/tools/dinner-plate/new" element={<DinnerPlateNewPage />} />
                  <Route path="/tools/dinner-plate/events/:id" element={<DinnerPlateEventPage />} />
                </Routes>
              </Layout>
            } />
          </Routes>
          <Toaster position="top-right" richColors />
          <ReminderPopup data={popupData} onClose={() => setPopupData(null)} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
